import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Menu, X, ChevronRight,
  SearchX, CalendarOff, Smartphone, Clock, EyeOff,
  Globe, MessageCircle, MapPin, CalendarClock, Bot,
  ShoppingBag, AppWindow, Headphones,
  Search, FileCode, Rocket, Sparkles,
  MessageCircle as WaIcon, Mail, Instagram, Linkedin, Facebook,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_presencia-digital-2/artifacts/19ezorhg_IMG_0946.jpeg";

const HERO_BG =
  "https://images.pexels.com/photos/22922098/pexels-photo-22922098.jpeg";

const RESULT_IMG =
  "https://images.pexels.com/photos/4428038/pexels-photo-4428038.jpeg";

const PAIN_POINTS = [
  { icon: SearchX, label: "Clientes que no encuentran tu negocio" },
  { icon: CalendarOff, label: "Reservas perdidas" },
  { icon: Smartphone, label: "Mala experiencia móvil" },
  { icon: Clock, label: "Falta de automatización" },
  { icon: EyeOff, label: "Baja visibilidad online" },
];

const SOLUTIONS = [
  { icon: Globe, title: "Página web", desc: "Diseño profesional optimizado para convertir visitas en clientes." },
  { icon: MessageCircle, title: "WhatsApp", desc: "Botón directo y respuestas rápidas para comunicación instantánea." },
  { icon: MapPin, title: "Google Maps", desc: "Aparece en búsquedas locales con tu ubicación destacada." },
  { icon: CalendarClock, title: "Reservas", desc: "Tus clientes reservan online, tú gestionas desde un solo lugar." },
  { icon: Bot, title: "Automatización", desc: "Respuestas, recordatorios y flujos automáticos 24/7." },
  { icon: ShoppingBag, title: "Tienda online", desc: "Vende productos y servicios con pagos seguros integrados." },
  { icon: AppWindow, title: "Aplicación móvil", desc: "Lleva tu negocio al siguiente nivel con tu propia app." },
  { icon: Headphones, title: "Asistente virtual", desc: "IA que atiende, responde dudas y capta oportunidades." },
];

const STEPS = [
  { n: "01", icon: Search, title: "Analizamos tu negocio", desc: "Entendemos tu sector, tus clientes y tus objetivos digitales." },
  { n: "02", icon: FileCode, title: "Creamos un prototipo optimizado", desc: "Diseñamos un sistema personalizado a tu medida." },
  { n: "03", icon: Rocket, title: "Activamos tu presencia online", desc: "Lo lanzamos al mundo y empiezas a recibir clientes." },
];

const NAV_ITEMS = [
  { label: "Inicio", href: "#inicio" },
  { label: "Soluciones", href: "#soluciones" },
  { label: "Sobre Nosotros", href: "#sobre" },
];

export default function Welcome() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // SEO
  useEffect(() => {
    const prev = document.title;
    document.title = "En Línea Madrid — Tu próximo cliente ya te está buscando";
    const ensure = (sel, create) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const setMeta = (name, content, prop = "name") => {
      const sel = `meta[${prop}="${name}"]`;
      const el = ensure(sel, () => {
        const m = document.createElement("meta");
        m.setAttribute(prop, name);
        return m;
      });
      el.setAttribute("content", content);
    };
    setMeta("description", "Activa tu presencia digital en Madrid. Web, WhatsApp, reservas, Google Maps, automatización y más, hechos por En Línea Madrid.");
    setMeta("og:title", "En Línea Madrid — Tu próximo cliente ya te está buscando", "property");
    setMeta("og:description", "Soluciones digitales modernas para negocios reales en Madrid.", "property");
    setMeta("og:image", HERO_BG, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:locale", "es_ES", "property");
    return () => { document.title = prev; };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMenuOpen(false);
  };

  return (
    <div id="inicio" className="bg-[#0f172a] text-white antialiased selection:bg-[#9b2226] selection:text-white overflow-x-hidden">
      <style>{`
        @keyframes elm-fade-up { from { opacity:0; transform: translateY(18px); } to { opacity:1; transform: none; } }
        .elm-fade-up { animation: elm-fade-up .9s cubic-bezier(.2,.7,.2,1) both; }
        .elm-stagger > * { opacity: 0; animation: elm-fade-up .8s cubic-bezier(.2,.7,.2,1) forwards; }
        .elm-stagger > *:nth-child(1){ animation-delay: .05s; }
        .elm-stagger > *:nth-child(2){ animation-delay: .12s; }
        .elm-stagger > *:nth-child(3){ animation-delay: .19s; }
        .elm-stagger > *:nth-child(4){ animation-delay: .26s; }
        .elm-stagger > *:nth-child(5){ animation-delay: .33s; }
        .elm-stagger > *:nth-child(6){ animation-delay: .40s; }
        .elm-stagger > *:nth-child(7){ animation-delay: .47s; }
        .elm-stagger > *:nth-child(8){ animation-delay: .54s; }
        @media (prefers-reduced-motion: reduce) {
          .elm-fade-up, .elm-stagger > * { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
        .elm-grid-bg-dark {
          background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0);
          background-size: 28px 28px;
        }
        .elm-card-hover { transition: transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s, border-color .35s, background-color .35s; }
        .elm-card-hover:hover { transform: translateY(-4px); box-shadow: 0 18px 50px -18px rgba(155,34,38,.35); border-color: rgba(155,34,38,.4); background-color: rgba(255,255,255,.06); }
        .focusring:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(155,34,38,.65), 0 0 0 5px rgba(15,23,42,1); border-radius: 9999px; }
      `}</style>

      {/* NAVBAR */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-slate-950/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <button
            onClick={() => scrollTo("inicio")}
            className="flex items-center gap-2.5 focusring"
            data-testid="nav-logo"
            aria-label="En Línea Madrid"
          >
            <img src={LOGO_URL} alt="" className="h-9 w-9 md:h-10 md:w-10 rounded-lg object-contain bg-white" />
            <div className="leading-none text-left">
              <div className="font-[Outfit] font-extrabold text-white text-sm md:text-base">
                En<span className="text-[#e76f72]">Línea</span>
              </div>
              <div className="text-[9px] md:text-[10px] tracking-[0.3em] text-slate-400 font-medium">MADRID</div>
            </div>
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9 text-[15px] text-slate-300 font-medium">
            {NAV_ITEMS.map((it) => (
              <button
                key={it.label}
                onClick={() => scrollTo(it.href.slice(1))}
                className="hover:text-white transition-colors focusring"
                data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {it.label}
              </button>
            ))}
            <Link
              to="/vendedores"
              className="hover:text-white transition-colors focusring"
              data-testid="nav-vendedores"
            >
              Vendedores
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/acceso"
              data-testid="nav-login"
              className="hidden md:inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/15 text-white text-sm font-semibold hover:bg-white/10 transition-colors focusring"
            >
              Iniciar sesión
            </Link>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden h-10 w-10 rounded-full border border-white/15 grid place-items-center hover:bg-white/10 focusring"
              data-testid="nav-toggle"
              aria-label="Abrir menú"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-white/10" data-testid="nav-mobile-menu">
            <nav className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-2">
              {NAV_ITEMS.map((it) => (
                <button
                  key={it.label}
                  onClick={() => scrollTo(it.href.slice(1))}
                  className="text-left px-3 py-3 rounded-xl text-slate-200 hover:bg-white/5 font-medium"
                  data-testid={`nav-mobile-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {it.label}
                </button>
              ))}
              <Link
                to="/vendedores"
                onClick={() => setMenuOpen(false)}
                className="px-3 py-3 rounded-xl text-slate-200 hover:bg-white/5 font-medium"
                data-testid="nav-mobile-vendedores"
              >
                Vendedores
              </Link>
              <Link
                to="/acceso"
                onClick={() => setMenuOpen(false)}
                className="mt-1 px-3 py-3 rounded-xl bg-white/10 text-white font-semibold text-center"
                data-testid="nav-mobile-login"
              >
                Iniciar sesión
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${HERO_BG})`,
          }}
          aria-hidden="true"
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-slate-950/85 via-slate-950/55 to-slate-950" aria-hidden="true" />
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-slate-950/60 via-transparent to-slate-950/40" aria-hidden="true" />
        <div className="absolute inset-0 z-10 elm-grid-bg-dark opacity-30" aria-hidden="true" />
        {/* Red glow */}
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#9b2226]/25 blur-3xl z-10" aria-hidden="true" />

        <div className="relative z-20 max-w-7xl mx-auto px-5 md:px-10 pt-28 pb-20 text-center">
          <div className="elm-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/80 text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e76f72] animate-pulse" />
              Soluciones digitales · Madrid
            </div>

            <h1 className="mt-7 font-[Outfit] font-black tracking-tighter text-white leading-[1.02] text-[40px] sm:text-6xl md:text-7xl lg:text-[88px] xl:text-[104px] mx-auto max-w-5xl">
              Tu próximo cliente <br className="hidden sm:block" />
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-white via-white to-[#e76f72] bg-clip-text text-transparent">
                  ya te está buscando.
                </span>
              </span>
            </h1>

            <p className="mt-7 mx-auto max-w-2xl text-slate-300 text-base sm:text-lg md:text-xl leading-relaxed">
              Muchos negocios pierden clientes todos los días por no tener una presencia online
              moderna, rápida y optimizada.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate("/onboarding")}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-[#9b2226] hover:bg-[#7a1a1e] text-white font-bold text-base transition-all focusring shadow-[0_18px_50px_-12px_rgba(155,34,38,0.65)] hover:scale-[1.02]"
              >
                Activar mi presencia online
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => scrollTo("soluciones")}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full border border-white/20 text-white font-semibold text-base hover:bg-white/10 transition-colors focusring"
              >
                Ver soluciones digitales
              </button>
            </div>
          </div>
        </div>

        {/* Subtle scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 hidden sm:flex flex-col items-center gap-2 text-white/40 text-[10px] uppercase tracking-[0.3em] animate-pulse" aria-hidden="true">
          Descubre
          <span className="w-px h-8 bg-white/30" />
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="relative py-24 md:py-32 bg-slate-950">
        <div className="absolute inset-0 elm-grid-bg-dark opacity-30" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#e76f72]">El problema</div>
            <h2 className="mt-3 font-[Outfit] font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Hoy la primera impresión <br className="hidden md:block" />
              de un negocio es <span className="text-[#e76f72]">digital</span>.
            </h2>
            <p className="mt-5 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
              Si no estás visible, si tu web no convierte o si tu cliente no puede contactar
              en segundos, ya has perdido la venta.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 elm-stagger">
            {PAIN_POINTS.map(({ icon: Icon, label }, i) => (
              <article
                key={label}
                className={`relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 elm-card-hover ${
                  i === 0 ? "lg:col-span-2" : ""
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#9b2226]/15 text-[#e76f72] grid place-items-center mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-[Outfit] font-semibold text-white text-lg leading-snug">
                  {label}
                </h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUCIONES */}
      <section id="soluciones" className="relative py-24 md:py-32 bg-gradient-to-b from-slate-950 via-[#13121f] to-slate-950">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#9b2226]/15 blur-3xl" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#e76f72]">Soluciones En Línea</div>
            <h2 className="mt-3 font-[Outfit] font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Creamos soluciones digitales <br className="hidden md:block" />
              modernas para <span className="text-[#e76f72]">negocios reales</span>.
            </h2>
            <p className="mt-5 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
              Un sistema integral, llave en mano, listo para captar, automatizar y vender desde el primer día.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 elm-stagger">
            {SOLUTIONS.map(({ icon: Icon, title, desc }) => (
              <article
                key={title}
                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-7 elm-card-hover relative overflow-hidden"
                data-testid={`solution-${title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#9b2226]/20 text-[#e76f72] grid place-items-center mb-5 group-hover:bg-[#9b2226] group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-[Outfit] font-bold text-white text-lg md:text-xl leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-slate-400 text-[13.5px] leading-relaxed">
                  {desc}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-14 flex justify-center">
            <button
              onClick={() => navigate("/onboarding")}
              data-testid="solutions-cta"
              className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-white text-[#0f172a] font-bold text-base hover:bg-slate-100 transition-colors focusring"
            >
              Ver propuesta para mi negocio
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="sobre" className="relative py-24 md:py-32 bg-slate-950">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#e76f72]">Cómo funciona</div>
            <h2 className="mt-3 font-[Outfit] font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Tres pasos para <span className="text-[#e76f72]">activar</span> tu negocio.
            </h2>
            <p className="mt-5 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
              Un proceso ágil, transparente y centrado en resultados reales.
            </p>
          </div>

          <ol className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 elm-stagger">
            {STEPS.map(({ n, icon: Icon, title, desc }) => (
              <li
                key={n}
                className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-10 elm-card-hover overflow-hidden"
              >
                <div className="font-[Outfit] font-black text-[100px] md:text-[120px] leading-none bg-gradient-to-br from-white/15 to-white/0 bg-clip-text text-transparent absolute top-3 right-5 select-none pointer-events-none">
                  {n}
                </div>
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-[#9b2226] text-white grid place-items-center mb-6">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-[Outfit] font-bold text-white text-xl md:text-2xl leading-tight">
                    {title}
                  </h3>
                  <p className="mt-3 text-slate-400 text-[14.5px] leading-relaxed">
                    {desc}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* RESULTADO */}
      <section className="relative py-24 md:py-32 bg-gradient-to-b from-slate-950 to-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          {/* Image */}
          <div className="lg:col-span-6 relative">
            <div className="absolute -inset-6 rounded-[40px] bg-gradient-to-br from-[#9b2226]/30 via-transparent to-white/10 blur-2xl" aria-hidden="true" />
            <div
              className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-3xl bg-cover bg-center border border-white/15 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
              style={{ backgroundImage: `url(${RESULT_IMG})` }}
              role="img"
              aria-label="Negocios modernos y clientes interactuando con sus móviles"
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 rounded-2xl bg-slate-950/60 backdrop-blur-md border border-white/10 px-4 py-3">
                <Sparkles className="w-4 h-4 text-[#e76f72] shrink-0" />
                <span className="text-[12px] text-white/85 font-medium">
                  Negocios de Madrid creciendo con En Línea
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#e76f72]">El resultado</div>
            <h2 className="mt-3 font-[Outfit] font-black text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.05]">
              Más visibilidad. <br />
              Más clientes. <br />
              <span className="text-[#e76f72]">Más oportunidades.</span>
            </h2>
            <p className="mt-6 text-slate-400 text-base md:text-lg max-w-xl leading-relaxed">
              Tu negocio en Madrid, en línea, listo para crecer.
              Activa hoy tu presencia digital y empieza a recibir clientes esta misma semana.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate("/onboarding")}
                data-testid="result-cta-primary"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-[#9b2226] hover:bg-[#7a1a1e] text-white font-bold text-base transition-all focusring shadow-[0_18px_50px_-12px_rgba(155,34,38,0.65)] hover:scale-[1.02]"
              >
                Empezar ahora <ArrowRight className="w-5 h-5" />
              </button>
              <Link
                to="/contacto"
                data-testid="result-cta-secondary"
                className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full border border-white/20 text-white font-semibold text-base hover:bg-white/10 transition-colors focusring"
              >
                Hablar con un asesor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black text-slate-400 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            {/* Brand */}
            <div className="md:col-span-5">
              <button
                onClick={() => scrollTo("inicio")}
                className="flex items-center gap-2.5"
                data-testid="footer-logo"
              >
                <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-lg object-contain bg-white" />
                <div className="leading-none text-left">
                  <div className="font-[Outfit] font-extrabold text-white text-base">
                    En<span className="text-[#e76f72]">Línea</span>
                  </div>
                  <div className="text-[10px] tracking-[0.3em] text-slate-500 font-medium">MADRID</div>
                </div>
              </button>
              <p className="mt-5 text-slate-500 text-sm max-w-sm leading-relaxed">
                Soluciones digitales modernas para negocios reales en Madrid.
                Activa tu presencia online y crece con nosotros.
              </p>
              <div className="mt-6 flex items-center gap-2">
                <a href="#" data-testid="footer-instagram" aria-label="Instagram" className="h-10 w-10 rounded-full border border-white/10 grid place-items-center hover:bg-white/10 hover:text-white transition-colors">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="#" data-testid="footer-linkedin" aria-label="LinkedIn" className="h-10 w-10 rounded-full border border-white/10 grid place-items-center hover:bg-white/10 hover:text-white transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href="#" data-testid="footer-facebook" aria-label="Facebook" className="h-10 w-10 rounded-full border border-white/10 grid place-items-center hover:bg-white/10 hover:text-white transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Links columns */}
            <div className="md:col-span-2">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-300">Navegación</div>
              <ul className="mt-5 space-y-3 text-sm">
                <li><button onClick={() => scrollTo("inicio")} className="hover:text-white" data-testid="footer-link-inicio">Inicio</button></li>
                <li><button onClick={() => scrollTo("soluciones")} className="hover:text-white" data-testid="footer-link-soluciones">Soluciones</button></li>
                <li><button onClick={() => scrollTo("sobre")} className="hover:text-white" data-testid="footer-link-sobre">Sobre nosotros</button></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-300">Acceso</div>
              <ul className="mt-5 space-y-3 text-sm">
                <li><Link to="/acceso" className="hover:text-white" data-testid="footer-login">Inicio de sesión</Link></li>
                <li><Link to="/vendedores" className="hover:text-white" data-testid="footer-vendedores-landing">Vendedores</Link></li>
                <li><Link to="/vendedores/login" className="hover:text-white" data-testid="footer-vendedores-login">Acceso vendedores</Link></li>
                <li><Link to="/admin/login" className="hover:text-white" data-testid="footer-admin">Admin</Link></li>
              </ul>
            </div>

            <div className="md:col-span-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-300">Contacto</div>
              <ul className="mt-5 space-y-3 text-sm">
                <li>
                  <a
                    href="https://wa.me/34685055202"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 hover:text-white"
                    data-testid="footer-whatsapp"
                  >
                    <WaIcon className="w-4 h-4 text-[#25D366]" /> WhatsApp
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:enlineamadrid@gmail.com"
                    className="inline-flex items-center gap-2 hover:text-white"
                    data-testid="footer-email"
                  >
                    <Mail className="w-4 h-4" /> enlineamadrid@gmail.com
                  </a>
                </li>
                <li>
                  <Link to="/contacto" className="hover:text-white" data-testid="footer-contacto">
                    Más formas de contacto
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-slate-500">
            <div>© {new Date().getFullYear()} En Línea Madrid · Madrid, España. Todos los derechos reservados.</div>
            <div className="flex items-center gap-5">
              <Link to="/privacidad" className="hover:text-white" data-testid="footer-privacidad">Privacidad</Link>
              <Link to="/privacidad" className="hover:text-white" data-testid="footer-legal">Información legal</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
